import React, {ReactElement, useEffect} from "react"
import {Claims, useAccessToken, useUserType} from "../../services/auth"
import {kebabCase} from "lodash"
import classNames from "classnames"
import numeral from "numeral"
import {Cents} from "../../resources/transaction"
import moment from "moment"
import {usePaging} from "../../hooks/usePaging"
import PagingNavBar from "../PagingNavBar/PagingNavBar"
import SearchBox from "../SearchBox/SearchBox"
import {useQueryState} from "use-location-state"
import {downloadFile} from "../../utilities/file"
import {useToasts} from "react-toast-notifications"
import DatePickerWithPresets, {DatePickerPresetKeys} from "../DatePicker/DatePicker"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {startCase} from "lodash"
import {
	DataTable,
	DataTableActionHeader,
	DataTableBody,
	DataTableCard,
	DataTableCell,
	DataTableHead,
	DataTableRow,
	TablePending,
} from "../DataTable/DataTable"
import {CurrencyRangeFilter} from "../Filter/CurrencyRangeFilter"
import {AccountHold, AccountHoldsStatus, getAccountHoldsRequest, releaseAccountHold} from "../../resources/accountHold"
import {ActionButton, ActionItem} from "../ActionButton/ActionButton"
import {TitleBarAction} from "../TitleBar/TitleBar"
import ReactTooltip from "react-tooltip"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {findOrgs} from "../../resources/org"
import {ErrorDocument} from "../../resources/common"
import {Org} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"

export enum AccountHoldsColumns {
	id = "Id",
	amount = "Amount",
	status = "Status",
	createdAt = "Created At",
	expiredAt = "Expired At",
	actions = "Actions",
}

type AllowedAccountHoldsColumns =
	| AccountHoldsColumns.id
	| AccountHoldsColumns.amount
	| AccountHoldsColumns.status
	| AccountHoldsColumns.createdAt
	| AccountHoldsColumns.expiredAt
	| AccountHoldsColumns.actions

type RefreshFunc = () => void

interface AccountHoldsProps {
	accountId?: string
	limit?: number
	isUsingPaging?: boolean
	enableSearch?: boolean
	enableDirectionFilter?: boolean
	enableOrgFilter?: boolean
	token?: string
	includedColumns: Array<AllowedAccountHoldsColumns>
	excludeGlTransaction?: boolean
	enableTitle?: boolean
	fullHeight?: boolean
	disableShadow?: boolean
	dataTableClassName?: string
	enableTypeFilter?: boolean
	enableAmountFilter?: boolean
	enableDateFilter?: boolean
	enableExport?: boolean
	title?: string
	claims?: Claims
	titleClassName?: string
	className?: string
	hideMinHeight?: boolean
	disabled?: boolean
}

function AccountHoldRow({
	hold,
	includedColumns,
	refresh,
}: {
	hold: AccountHold
	includedColumns: Array<AllowedAccountHoldsColumns>
	refresh: RefreshFunc
}) {
	const status = hold.attributes.status
	const amount = hold.attributes.amount
	const showActions = status === AccountHoldsStatus.Active
	const accessToken = useAccessToken()
	const [, releaseAction] = useAsyncResultIdle(releaseAccountHold)

	const onRelease = async () => {
		const result = await releaseAction(accessToken, hold.relationships.account.data.id, hold.id)
		result.match(
			() => {
				refresh()
			},
			(_) => {
				// Handle error
			}
		)
	}

	const contentColumns: Partial<Record<AllowedAccountHoldsColumns, ReactElement>> = {}

	if (includedColumns.includes(AccountHoldsColumns.id)) {
		contentColumns[AccountHoldsColumns.id] = <DataTableCell>{hold.id}</DataTableCell>
	}

	if (includedColumns.includes(AccountHoldsColumns.amount)) {
		contentColumns[AccountHoldsColumns.amount] = (
			<DataTableCell>{numeral(amount / 100).format("$0,0.00")}</DataTableCell>
		)
	}

	if (includedColumns.includes(AccountHoldsColumns.status)) {
		const isReleased = startCase(status) === "Released"
		const tooltipId = `box-tooltip-${kebabCase(status)}-${hold.id}`

		contentColumns[AccountHoldsColumns.status] = (
			<DataTableCell>
				<div
					data-tip
					data-for={isReleased ? tooltipId : undefined}
					className={classNames("accountHolds-status", kebabCase(status))}
				>
					{startCase(status)}
				</div>

				{isReleased && (
					<ReactTooltip id={tooltipId} className="tooltip-info text-tooltip-container" effect="solid" place="top">
						<p>{moment(hold.attributes.releasedAt).format("MM/DD/YYYY")}</p>
					</ReactTooltip>
				)}
			</DataTableCell>
		)
	}

	if (includedColumns.includes(AccountHoldsColumns.createdAt)) {
		contentColumns[AccountHoldsColumns.createdAt] = (
			<DataTableCell>{moment(hold.attributes.createdAt).format("L")}</DataTableCell>
		)
	}

	if (includedColumns.includes(AccountHoldsColumns.expiredAt)) {
		contentColumns[AccountHoldsColumns.expiredAt] = (
			<DataTableCell>{moment(hold.attributes.expiredAt).format("L")}</DataTableCell>
		)
	}

	if (includedColumns.includes(AccountHoldsColumns.actions)) {
		contentColumns[AccountHoldsColumns.actions] = (
			<DataTableCell>
				{showActions ? (
					<TitleBarAction>
						<ActionButton enableActions={true}>
							<ActionItem
								title={"Release Hold"}
								color={"#000"}
								icon={"music-player-button-play--music-audio"}
								onClick={onRelease}
							/>
						</ActionButton>
					</TitleBarAction>
				) : null}
			</DataTableCell>
		)
	}

	return <DataTableRow>{includedColumns.map((col, i) => contentColumns[col] ?? <td key={i} />)}</DataTableRow>
}

function AccountHoldsTable({
	holds,
	hasResults,
	hasPrev,
	hasNext,
	prev,
	next,
	isUsingPaging,
	includedColumns,
	fullHeight,
	isFiltering,
	hideMinHeight,
	disabled,
	refresh,
}: {
	holds: AccountHold[]
	hasResults: boolean
	hasPrev: boolean
	hasNext: boolean
	prev: () => void
	next: () => void
	isUsingPaging: boolean
	includedColumns: Array<AllowedAccountHoldsColumns>
	fullHeight: boolean
	isFiltering: boolean
	hideMinHeight?: boolean
	disabled?: boolean
	refresh: RefreshFunc
}) {
	const noContent = holds.length === 0

	return (
		<div>
			<DataTable
				isEmpty={noContent}
				fullHeight={fullHeight}
				stickyTitle={fullHeight}
				hideMinHeight={hideMinHeight}
				noContentText={isFiltering ? "No account holds found" : "No account holds yet"}
				disabled={disabled}
			>
				<DataTableHead>
					<DataTableRow>
						{includedColumns.map((col) => (
							<DataTableCell key={col}>{col}</DataTableCell>
						))}
					</DataTableRow>
				</DataTableHead>
				<DataTableBody>
					{holds.map((hold) => (
						<AccountHoldRow key={hold.id} hold={hold} includedColumns={includedColumns} refresh={refresh} />
					))}
				</DataTableBody>
			</DataTable>
			<PagingNavBar
				hasResults={hasResults}
				hasPrev={hasPrev}
				hasNext={hasNext}
				prev={prev}
				next={next}
				isShow={isUsingPaging}
			/>
		</div>
	)
}

export function AccountHolds({
	limit = 10,
	isUsingPaging = true,
	enableSearch = true,
	enableOrgFilter = false,
	token,
	accountId,
	includedColumns,
	enableTitle = true,
	fullHeight = true,
	disableShadow = false,
	enableAmountFilter = true,
	enableDateFilter = true,
	enableExport = true,
	dataTableClassName,
	title = "Account Holds",
	claims,
	titleClassName,
	className,
	hideMinHeight,
	disabled,
}: AccountHoldsProps) {
	const prefix = "hold"
	const userType = useUserType(claims)
	const accessToken = token ?? useAccessToken()
	const {addToast} = useToasts()
	const [accountHoldsRefreshToken, refreshAccountHolds] = useRefreshToken()
	const [query, setQuery] = useQueryState("query", "")
	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search Account Holds", 500, onSearch)
	const [since, setSince] = useQueryState(`${prefix}-filter[since]`, "")
	const [until, setUntil] = useQueryState(`${prefix}-filter[until]`, "")
	const [filteredByOrg, setFilteredByOrg] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])
	const [sortBy] = useQueryState(`${prefix}-sort`, "-createdAt")
	const [fromAmount, setFromAmount] = useQueryState<Cents | "">(`${prefix}-filter[fromAmount]`, "")
	const [toAmount, setToAmount] = useQueryState<Cents | "">(`${prefix}-filter[toAmount]`, "")

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		getAccountHolds,
		(x) => x.data.length,
		[query, since, until, sortBy, accountHoldsRefreshToken, fromAmount, toAmount],
		`${prefix}-`,
		true
	)

	const isFiltering =
		query !== "" || since !== "" || until !== "" || filteredByOrg.length !== 0 || fromAmount !== "" || toAmount !== ""

	const orgs =
		(userType === "unit" || userType === "bank") && enableOrgFilter
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = OrgFilter(orgs, setFilteredByOrg, filteredByOrg, reset, limit)

	const dateTime = (
		<DatePickerWithPresets
			presets={[
				DatePickerPresetKeys.allTime,
				DatePickerPresetKeys.lastMonth,
				DatePickerPresetKeys.last3Months,
				DatePickerPresetKeys.last6Months,
				DatePickerPresetKeys.last30Days,
				DatePickerPresetKeys.last7Days,
				DatePickerPresetKeys.custom,
			]}
			onDateChanged={(s, u) => {
				setSince(s)
				setUntil(u)
				reset(limit)
			}}
		/>
	)

	const amountFilter = (
		<CurrencyRangeFilter
			placeholder="Amount"
			presets={[]}
			from={fromAmount}
			setFrom={(value) => {
				setFromAmount(value)
				reset(limit)
			}}
			to={toAmount}
			setTo={(value) => {
				setToAmount(value)
				reset(limit)
			}}
		/>
	)

	function getAccountHolds(offset: number, limit: number) {
		return getAccountHoldsRequest({
			accessToken,
			offset,
			limit,
			searchQuery: enableSearch ? query : "",
			since,
			until,
			accountId,
			requestType: "json",
			sort: sortBy,
		})
	}

	async function exportAccountHolds() {
		const response = await getAccountHoldsRequest({
			accessToken,
			offset: 0,
			searchQuery: enableSearch ? query : "",
			since,
			until,
			accountId,
			requestType: "csv",
			sort: sortBy,
		})

		response.match(
			(d) => downloadFile("AccountHolds.csv", d, "text/csv"),
			() => addToast("Something went wrong", {appearance: "warning"})
		)

		return true
	}

	useEffect(() => {
		let isActive = true
		if (isActive) {
			result.match(
				() => null,
				() => setIsSearchLoading(false),
				() => setIsSearchLoading(false)
			)
		}
		return () => {
			isActive = false
		}
	}, [result])

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	return (
		<DataTableCard
			className={classNames(
				"accountHolds-card",
				disableShadow && "accountHolds-card-disable-shadow",
				dataTableClassName
			)}
			cardClassName={className}
		>
			<DataTableActionHeader
				searchBox={enableSearch ? searchBox : null}
				dateTimePicker={enableDateFilter ? dateTime : null}
				filters={[...(enableOrgFilter ? [orgFilter] : []), ...(enableAmountFilter ? [amountFilter] : [])]}
				exportFunc={enableExport ? exportAccountHolds : undefined}
				title={enableTitle ? title : null}
				enableSticky={fullHeight}
				titleClassName={classNames(titleClassName, disabled && "disabled")}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: data}) => (
					<AccountHoldsTable
						holds={data.data}
						hasResults={hasResults}
						hasPrev={hasPrev}
						hasNext={hasNext}
						prev={prev}
						next={next}
						isUsingPaging={isUsingPaging}
						includedColumns={includedColumns}
						fullHeight={fullHeight}
						isFiltering={isFiltering}
						hideMinHeight={hideMinHeight}
						disabled={disabled}
						refresh={refreshAccountHolds}
					/>
				)}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
